import React, { useState, useRef, useEffect } from 'react';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Eye, Search, Users, ChevronDown, X, Building2 } from 'lucide-react';

/**
 * UserImpersonationSelector - Dropdown to select a user to impersonate
 * 
 * Only visible to Super Admins and Founders.
 * Allows searching/filtering users and starting impersonation.
 */
export const UserImpersonationSelector = () => {
  const { 
    canImpersonate, 
    impersonatableUsers, 
    isImpersonating,
    loading,
    startImpersonation,
    fetchImpersonatableUsers
  } = useImpersonation();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if user can't impersonate or is already impersonating
  if (!canImpersonate || isImpersonating) {
    return null;
  }

  // Filter users based on search and role
  const filteredUsers = impersonatableUsers.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.client_names || []).some(name => name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    
    return matchesSearch && matchesRole;
  });

  // Get unique roles for filter
  const uniqueRoles = [...new Set(impersonatableUsers.map(u => u.role))];

  const handleSelectUser = async (userId) => {
    const success = await startImpersonation(userId);
    if (success) {
      setIsOpen(false);
      setSearchTerm('');
      // Don't reload - let React re-render with new impersonation state
      // The impersonation header is now set, so subsequent API calls will use it
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchImpersonatableUsers(); // Refresh user list
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        onClick={handleOpen}
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 text-slate-700 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800"
        data-testid="impersonation-selector-trigger"
      >
        <Eye className="w-4 h-4" />
        <span>View as User</span>
        <ChevronDown className="w-3 h-3 ml-auto" />
      </Button>

      {/* Dropdown Panel - positioned above the button since it's in sidebar footer */}
      {isOpen && (
        <div 
          className="absolute left-0 bottom-full mb-2 w-96 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50"
          data-testid="impersonation-selector-dropdown"
        >
          {/* Header */}
          <div className="p-3 border-b border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-white">
                <Users className="w-4 h-4" />
                <span className="font-medium text-sm">Select User to View As</span>
              </div>
              <Button
                onClick={() => setIsOpen(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name, email, or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-600 text-white text-sm placeholder:text-slate-500"
                data-testid="impersonation-search"
              />
            </div>

            {/* Role Filter */}
            <div className="flex gap-1 mt-2 flex-wrap">
              <button
                onClick={() => setSelectedRole('all')}
                className={`px-2 py-1 text-xs rounded ${
                  selectedRole === 'all' 
                    ? 'bg-sky-500 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                All
              </button>
              {uniqueRoles.map(role => (
                <button
                  key={role}
                  onClick={() => setSelectedRole(role)}
                  className={`px-2 py-1 text-xs rounded capitalize ${
                    selectedRole === role 
                      ? 'bg-sky-500 text-white' 
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {role.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* User List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                Loading users...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                No users found
              </div>
            ) : (
              <div className="py-1">
                {filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectUser(user.id)}
                    className="w-full px-3 py-2.5 flex items-start gap-3 hover:bg-slate-700 transition-colors text-left"
                    data-testid={`impersonate-user-${user.id}`}
                  >
                    {/* Avatar */}
                    <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-white">
                        {user.full_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white text-sm truncate">
                          {user.full_name}
                        </span>
                        <span className="px-1.5 py-0.5 bg-slate-600 text-slate-300 text-xs rounded flex-shrink-0">
                          {user.role_display}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 truncate">
                        {user.email}
                      </div>
                      {user.client_names?.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                          <Building2 className="w-3 h-3" />
                          <span className="truncate">{user.client_names.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-2 border-t border-slate-700 text-xs text-slate-500 text-center">
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} available
          </div>
        </div>
      )}
    </div>
  );
};

export default UserImpersonationSelector;
